(() => {
  class GlissnGuideComparison extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.range = this.querySelector('input[type="range"]');
      this.frame = this.querySelector('.gg-comparison-frame');

      if (!this.range || !this.frame) return;

      this.update = () => {
        const value = Number(this.range.value);
        const afterValue = 100 - value;
        this.frame.style.setProperty('--position', `${value}%`);

        let valueText = `${value}% before and ${afterValue}% after`;
        if (value === 0) valueText = 'After photo only';
        if (value === 50) valueText = 'Half before and half after';
        if (value === 100) valueText = 'Before photo only';
        this.range.setAttribute('aria-valuetext', valueText);
      };

      this.resize = () => {
        this.frame.style.setProperty('--comparison-width', `${this.frame.clientWidth}px`);
      };

      this.range.addEventListener('input', this.update);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.resize);
        this.observer.observe(this.frame);
      } else {
        window.addEventListener('resize', this.resize);
      }

      this.update();
      this.resize();
    }

    disconnectedCallback() {
      this.range?.removeEventListener('input', this.update);
      this.observer?.disconnect();
      window.removeEventListener('resize', this.resize);
    }
  }

  class GlissnGuideQuiz extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.guide = this.closest('[data-glissn-guide]');
      this.buttons = [...this.querySelectorAll('[data-quiz-answer]')];
      this.result = this.querySelector('[data-quiz-result]');
      this.resultCopy = this.querySelector('[data-quiz-result-copy]');
      this.resultMedia = this.querySelector('[data-quiz-result-media]');
      this.resultImage = this.querySelector('[data-quiz-result-image]');
      this.resultDetails = this.querySelector('[data-quiz-result-details]');
      this.resultMadeFor = this.querySelector('[data-result-made-for]');
      this.resultDoes = this.querySelector('[data-result-does]');
      this.resultVibe = this.querySelector('[data-result-vibe]');
      this.resultActions = this.querySelector('[data-quiz-result-actions]');
      this.resultsLink = this.querySelector('[data-quiz-results-link]');
      this.directionsLink = this.querySelector('[data-quiz-directions-link]');
      this.resetButton = this.querySelector('[data-quiz-reset]');
      this.originalCopy = this.resultCopy?.innerHTML || '';

      this.handleAnswer = (event) => this.selectMatch(event.currentTarget);
      this.handleReset = () => this.reset();
      this.handleDirections = () => {
        const tab = document.getElementById(this.directionsLink?.dataset.tabTarget || '');
        tab?.click();
      };

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleAnswer);
      });

      this.resetButton?.addEventListener('click', this.handleReset);
      this.directionsLink?.addEventListener('click', this.handleDirections);
    }

    selectMatch(button) {
      if (!button) return;

      const key = button.dataset.matchKey || '';
      this.buttons.forEach((option) => {
        option.setAttribute('aria-pressed', String(option === button));
      });

      if (key) {
        this.dataset.match = key;
        if (this.guide) this.guide.dataset.match = key;
      }

      if (this.resultCopy) {
        const heading = document.createElement('strong');
        const copy = document.createElement('span');
        heading.textContent = button.dataset.product || '';
        copy.textContent = button.dataset.copy || '';
        this.resultCopy.replaceChildren(heading, copy);
      }

      if (this.result) this.result.dataset.hasMatch = 'true';
      if (this.resultImage && button.dataset.image) {
        this.resultImage.src = button.dataset.image;
        this.resultImage.alt = `${button.dataset.product || "GLISS'N"} formula`;
      }
      if (this.resultMedia) this.resultMedia.hidden = !button.dataset.image;
      if (this.resultMadeFor) this.resultMadeFor.textContent = button.dataset.madeFor || '';
      if (this.resultDoes) this.resultDoes.textContent = button.dataset.does || '';
      if (this.resultVibe) this.resultVibe.textContent = button.dataset.vibe || '';
      if (this.resultDetails) this.resultDetails.hidden = false;

      if (this.resultsLink && button.dataset.resultsTarget) {
        this.resultsLink.href = button.dataset.resultsTarget;
      }

      if (this.directionsLink) {
        this.directionsLink.dataset.tabTarget = button.dataset.tabTarget || '';
      }

      if (this.resultActions) this.resultActions.hidden = false;
      if (this.resetButton) this.resetButton.hidden = false;
    }

    reset() {
      this.buttons.forEach((button) => button.setAttribute('aria-pressed', 'false'));
      delete this.dataset.match;
      if (this.guide) delete this.guide.dataset.match;
      if (this.resultCopy) this.resultCopy.innerHTML = this.originalCopy;
      if (this.result) delete this.result.dataset.hasMatch;
      if (this.resultMedia) this.resultMedia.hidden = true;
      if (this.resultImage) {
        this.resultImage.removeAttribute('src');
        this.resultImage.alt = '';
      }
      if (this.resultDetails) this.resultDetails.hidden = true;
      if (this.resultMadeFor) this.resultMadeFor.textContent = '';
      if (this.resultDoes) this.resultDoes.textContent = '';
      if (this.resultVibe) this.resultVibe.textContent = '';
      if (this.resultActions) this.resultActions.hidden = true;
      if (this.directionsLink) delete this.directionsLink.dataset.tabTarget;
      if (this.resetButton) this.resetButton.hidden = true;
      this.buttons[0]?.focus();
    }

    disconnectedCallback() {
      this.buttons?.forEach((button) => button.removeEventListener('click', this.handleAnswer));
      this.resetButton?.removeEventListener('click', this.handleReset);
      this.directionsLink?.removeEventListener('click', this.handleDirections);
    }
  }

  class GlissnGuideTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.buttons = [...this.querySelectorAll('[role="tab"][data-tab]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"][data-panel]')];
      this.mobileLayout = window.matchMedia('(max-width: 649px)');

      this.activate = (button, moveFocus = false) => {
        if (!button) return;

        this.buttons.forEach((tab) => {
          const active = tab === button;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          panel.hidden = this.mobileLayout.matches ? false : panel.dataset.panel !== button.dataset.tab;
        });

        if (moveFocus) button.focus();
      };

      this.handleLayoutChange = () => {
        const selected = this.buttons.find((button) => button.getAttribute('aria-selected') === 'true') || this.buttons[0];
        this.activate(selected);
      };

      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.activate(button));
        button.addEventListener('keydown', (event) => {
          const currentIndex = this.buttons.indexOf(button);
          let nextIndex = null;

          if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.buttons.length;
          if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = this.buttons.length - 1;

          if (nextIndex !== null) {
            event.preventDefault();
            this.activate(this.buttons[nextIndex], true);
          }
        });
      });

      this.activate(this.buttons.find((button) => button.getAttribute('aria-selected') === 'true') || this.buttons[0]);
      this.mobileLayout.addEventListener?.('change', this.handleLayoutChange);
    }

    disconnectedCallback() {
      this.mobileLayout?.removeEventListener?.('change', this.handleLayoutChange);
    }
  }

  class GlissnGuideShare extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.button = this.querySelector('[data-share-guide]');
      this.status = this.querySelector('[data-share-status]');
      this.handleShare = this.share.bind(this);
      this.button?.addEventListener('click', this.handleShare);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleShare);
    }

    async copyLink(url) {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return;
      }

      const field = document.createElement('textarea');
      field.value = url;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }

    async share() {
      const shareData = {
        title: "The GLISS'N Guide",
        text: "Everything you need to choose, mix, and use GLISS'N!",
        url: window.location.href,
      };

      try {
        if (navigator.share) {
          await navigator.share(shareData);
          if (this.status) this.status.textContent = 'Thanks for sharing the gloss!';
          return;
        }

        await this.copyLink(shareData.url);
        if (this.status) this.status.textContent = 'Link copied! Send it to your favorite hair friend.';
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (this.status) this.status.textContent = 'Copy the link from your browser to share the Guide.';
      }
    }
  }

  if (!customElements.get('glissn-guide-comparison')) {
    customElements.define('glissn-guide-comparison', GlissnGuideComparison);
  }
  if (!customElements.get('glissn-guide-quiz')) {
    customElements.define('glissn-guide-quiz', GlissnGuideQuiz);
  }
  if (!customElements.get('glissn-guide-tabs')) {
    customElements.define('glissn-guide-tabs', GlissnGuideTabs);
  }
  if (!customElements.get('glissn-guide-share')) {
    customElements.define('glissn-guide-share', GlissnGuideShare);
  }

  const getVisibleHeaderOffset = () => {
    const candidates = [
      document.querySelector('#header-group'),
      document.querySelector('#header-component'),
      document.querySelector('#header-group .header-section'),
    ].filter(Boolean);

    return candidates.reduce((offset, element) => {
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      const pinned =
        style.position === 'sticky' ||
        style.position === 'fixed' ||
        element.getAttribute('data-sticky-state') === 'active';

      if (!pinned || rect.bottom <= 0 || rect.top > 1) return offset;
      return Math.max(offset, Math.round(rect.bottom));
    }, 0);
  };

  // Normalizes a CDN image URL to its base filename (strips size suffix and query)
  // so the same photo at different sizes is recognized as a duplicate across cards.
  const heroImageKey = (url) => {
    const file = (url || '').split('?')[0].split('/').pop() || '';
    return file.replace(/_\d+x\d*(?=\.)/, '');
  };

  const heroRegistries = new WeakMap();

  const initializeHeroRotator = (rotator) => {
    if (rotator.dataset.rotatorReady) return;
    rotator.dataset.rotatorReady = 'true';

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const layers = [...rotator.querySelectorAll('.gg-hero-frame-image')];
    const mobileImages = rotator.dataset.ggMobileImages || '';
    const useMobileImages = window.matchMedia('(max-width: 649px)').matches && mobileImages;
    const images = (useMobileImages || rotator.dataset.ggImages || '').split('|').filter(Boolean);
    const guide = rotator.closest('[data-glissn-guide]') || document.body;

    if (useMobileImages) {
      layers.forEach((layer, index) => {
        if (images[index]) layer.src = images[index];
      });
    }

    if (!heroRegistries.has(guide)) heroRegistries.set(guide, new Map());
    const registry = heroRegistries.get(guide);
    registry.set(rotator, heroImageKey(images[0] || layers[0]?.src));

    if (reduceMotion || images.length < 2 || layers.length < 2) return;

    let activeLayer = 0;
    let imageIndex = 0;
    let loading = false;
    let intervalId;
    let startTimeoutId;

    const shownElsewhere = (url) => {
      const key = heroImageKey(url);
      for (const [other, otherKey] of registry) {
        if (other !== rotator && otherKey === key) return true;
      }
      return false;
    };

    const rotate = () => {
      if (document.hidden || loading) return;

      let nextIndex = imageIndex;
      for (let step = 0; step < images.length; step += 1) {
        nextIndex = (nextIndex + 1) % images.length;
        if (nextIndex !== imageIndex && !shownElsewhere(images[nextIndex])) break;
      }
      if (nextIndex === imageIndex || shownElsewhere(images[nextIndex])) return;

      loading = true;
      imageIndex = nextIndex;
      const incomingLayer = activeLayer === 0 ? 1 : 0;
      const incomingImage = new Image();

      incomingImage.onload = () => {
        layers[incomingLayer].src = images[imageIndex];
        registry.set(rotator, heroImageKey(images[imageIndex]));
        window.requestAnimationFrame(() => {
          layers[incomingLayer].classList.add('is-active');
          layers[activeLayer].classList.remove('is-active');
          activeLayer = incomingLayer;
          loading = false;
        });
      };

      incomingImage.onerror = () => {
        loading = false;
      };

      incomingImage.src = images[imageIndex];
    };

    const delay = Number(rotator.dataset.ggDelay || 0);
    startTimeoutId = window.setTimeout(() => {
      rotate();
      intervalId = window.setInterval(rotate, 5500);
    }, 5500 + delay);

    rotator._ggStopRotator = () => {
      window.clearTimeout(startTimeoutId);
      window.clearInterval(intervalId);
    };
  };

  const initializeGuide = (guide) => {
    if (guide.dataset.behaviorReady) return;
    guide.dataset.behaviorReady = 'true';

    const mobileHero = window.matchMedia('(max-width: 649px)').matches;
    guide.querySelectorAll('[data-gg-hero-rotator]').forEach((rotator) => {
      if (!mobileHero || rotator.classList.contains('gg-hero-card--main')) initializeHeroRotator(rotator);
    });

    let frameRequested = false;
    const updateHeaderOffset = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(() => {
        guide.style.setProperty('--gg-header-offset', `${getVisibleHeaderOffset()}px`);
        frameRequested = false;
      });
    };

    updateHeaderOffset();
    window.addEventListener('scroll', updateHeaderOffset, { passive: true });
    window.addEventListener('resize', updateHeaderOffset);
    guide._ggUpdateHeaderOffset = updateHeaderOffset;

    const navLinks = [...guide.querySelectorAll('.gg-nav a[href^="#"]')];
    const sections = navLinks
      .map((link) => document.getElementById(decodeURIComponent(link.hash.slice(1))))
      .filter(Boolean);

    if ('IntersectionObserver' in window && sections.length) {
      guide._ggSectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (!visible) return;
          navLinks.forEach((link) => link.removeAttribute('aria-current'));
          const currentLink = navLinks.find(
            (link) => decodeURIComponent(link.hash.slice(1)) === visible.target.id,
          );
          currentLink?.setAttribute('aria-current', 'true');

          const navList = currentLink?.closest('ul');
          if (navList && currentLink) {
            const left = currentLink.offsetLeft - (navList.clientWidth - currentLink.clientWidth) / 2;
            navList.scrollTo({
              left: Math.max(0, left),
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          }
        },
        { rootMargin: '-30% 0px -60%', threshold: [0, 0.01, 0.25] },
      );

      sections.forEach((section) => guide._ggSectionObserver.observe(section));
    }
  };

  document.querySelectorAll('[data-glissn-guide]').forEach(initializeGuide);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-glissn-guide]').forEach(initializeGuide);
    if (event.target.matches?.('[data-glissn-guide]')) initializeGuide(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const guide = event.target.matches?.('[data-glissn-guide]')
      ? event.target
      : event.target.querySelector?.('[data-glissn-guide]');

    if (!guide) return;
    guide._ggSectionObserver?.disconnect();
    guide.querySelectorAll('[data-gg-hero-rotator]').forEach((rotator) => rotator._ggStopRotator?.());
    window.removeEventListener('scroll', guide._ggUpdateHeaderOffset);
    window.removeEventListener('resize', guide._ggUpdateHeaderOffset);
  });
})();
